import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import BlogPostCard, { type BlogCardPost } from '@/components/marketing/BlogPostCard'
import BlogSidebar from '@/components/marketing/BlogSidebar'
import { type CategoryNavItem } from '@/components/marketing/BlogCategoryNav'
import type { SharedMarketingProps } from '@/types/marketing'

interface BlogArchiveProps extends SharedMarketingProps {
  heading: string
  kind: 'category' | 'tag'
  description: string
  posts: BlogCardPost[]
  categories: CategoryNavItem[]
}

/** Archive listing for a single category or tag. */
export default function BlogArchive({ heading, kind, description, posts, categories }: BlogArchiveProps) {
  const activeSlug = kind === 'category' ? posts[0]?.category_slug ?? null : null

  return (
    <>
      <AtmosphericHero minHeight="44vh">
        <div className="mx-auto max-w-3xl text-center space-y-10">
          <ScrollReveal>
            <span className="hero-eyebrow" style={{ justifyContent: 'center' }}>
              {kind === 'tag' ? 'Tag' : 'Category'}
            </span>
          </ScrollReveal>
          <ScrollReveal delay={0.05}>
            <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}>{heading}</h1>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <p className="hero-body mx-auto">{description}</p>
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      {/* Full-width body — sticky sidebar + article grid. Same layout
          as /blog. */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid gap-10 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-14">
          <BlogSidebar
            categories={categories}
            activeSlug={activeSlug}
            hideActiveState={kind === 'tag'}
          />

          <div>
            {posts.length > 0 ? (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {posts.map((post, i) => (
                  <ScrollReveal key={post.slug} delay={i * 0.05}>
                    <BlogPostCard post={post} />
                  </ScrollReveal>
                ))}
              </div>
            ) : (
              <p className="text-white/48">No articles here yet — check back soon.</p>
            )}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-24">
        <ScrollReveal>
          <div className="marketing-final-cta mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-white">Ready to distribute your music?</h2>
            <p className="mt-3 text-white/64">
              Get your music on 100+ platforms. Plans from $15/year.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="/apply" className="marketing-button-primary">Apply Now</a>
              <a href="/blogs" className="marketing-button-secondary">All Articles</a>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </>
  )
}
